<template>
  <div class="paypal-pay-later-option flex" :class="wrapperClasses">
    <span class="paypal-pay-later-option__logo custom-icon icon-paypal"></span>
    <div class="paypal-pay-later-option__content content">
      <p class="content__message u-typography-helvetica u-text u-text--s">{{ paypalMessage }}</p>
      &nbsp;
      <button
        class="content__learn-more-button u-typography-helvetica u-text u-text--s"
        @click="openPayLaterInfoModal"
      >
        {{ $t('paypal.label.learnMore') }}
      </button>
    </div>
    <pay-pal-pay-later-info-modal
      ref="paypalModal"
      :country="country"
      :payment-amount="paymentAmountFormatted"
      :payment-part="paymentPart"
      :placement="placement"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import PayPalPayLaterInfoModal from '@/components/shipping/payment-method/PayPalPayLaterInfoModal.vue';

import { priceFormatterMixin } from '@/mixins/priceFormatterMixin';

import { types } from '@/store/types';
import { COUNTRY_ISO } from '@/constants/countries';
import { dividePrice } from '@/models/core/values';

const PAYPAL_MESSAGES = {
  home: {
    [COUNTRY_ISO.AUSTRALIA]: 'paypal.label.buyNowPayLater',
    [COUNTRY_ISO.FRANCE]: 'paypal.label.buyNowPayLater',
    [COUNTRY_ISO.GREAT_BRITAIN]: 'paypal.label.buyNowPayLater',
    [COUNTRY_ISO.UNITED_STATES]: 'paypal.label.buyNowPayLater'
  },
  cart: {
    [COUNTRY_ISO.AUSTRALIA]: 'paypal.message.cartAustralia',
    [COUNTRY_ISO.FRANCE]: 'paypal.message.cartFrance',
    [COUNTRY_ISO.GREAT_BRITAIN]: 'paypal.message.cartUK',
    [COUNTRY_ISO.UNITED_STATES]: 'paypal.message.cartUS'
  },
  payment: {
    [COUNTRY_ISO.AUSTRALIA]: 'paypal.message.paymentAustralia',
    [COUNTRY_ISO.FRANCE]: 'paypal.message.paymentFrance',
    [COUNTRY_ISO.GREAT_BRITAIN]: 'paypal.message.paymentUK',
    [COUNTRY_ISO.UNITED_STATES]: 'paypal.message.paymentUS'
  }
};

const LOGO_POSITION = {
  TOP: 'top',
  INLINE: 'inline'
};

export default {
  name: 'PayPalPayLater',
  components: { PayPalPayLaterInfoModal },
  mixins: [priceFormatterMixin],
  props: {
    paymentAmount: {
      type: Object,
      default: null
    },
    placement: {
      type: String,
      default: 'cart'
    },
    logoPosition: {
      type: String,
      default: LOGO_POSITION.INLINE
    }
  },
  computed: {
    ...mapGetters({
      country: types.getters.DELIVERY_COUNTRY
    }),
    paymentPart() {
      if (!this.paymentAmount) {
        return '';
      }

      const price =
        this.country === COUNTRY_ISO.GREAT_BRITAIN.toLowerCase()
          ? dividePrice(this.paymentAmount, 3)
          : dividePrice(this.paymentAmount, 4);

      return this.formatPrice(price);
    },
    paymentAmountFormatted() {
      return this.formatPrice(this.paymentAmount);
    },
    paypalMessage() {
      return this.placement === 'payment'
        ? this.$t(PAYPAL_MESSAGES.payment[this.country.toUpperCase()], [this.paymentPart])
        : this.$t(PAYPAL_MESSAGES[this.placement][this.country.toUpperCase()]);
    },
    wrapperClasses() {
      return {
        'paypal-pay-later-option--logo-top': this.logoPosition === LOGO_POSITION.TOP,
        'paypal-pay-later-option--logo-inline': this.logoPosition === LOGO_POSITION.INLINE
      };
    }
  },
  methods: {
    openPayLaterInfoModal() {
      this.$refs.paypalModal.open();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/icon.scss';

.paypal-pay-later-option {
  &__logo {
    margin-inline-end: 10px;
  }

  &__content {
    margin-top: 8px;
  }

  &--logo-inline {
    align-items: flex-start;
  }

  &--logo-top {
    flex-direction: column;

    .paypal-pay-later-option__content {
      margin-top: 0;
    }
  }
}

.content {
  color: var(--u-color-graphite);

  &__message {
    display: inline;
  }

  &__learn-more-button {
    display: inline-block;
    font-weight: 500;
    color: #0d56b7;
    background-color: initial;
    border: none;
    cursor: pointer;
  }
}

@media (min-width: 960px) {
  .paypal-pay-later-option {
    &__content {
      margin-top: 5px;
    }
  }
}
</style>
